import React, { type CSSProperties, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { CloseOutlined } from "@ant-design/icons";
import { Button, type ButtonProps } from "./Button";
import { Spin } from "./Spin";
import { cx } from "./fieldContext";
import { useUiText } from "./uiText";
import { drawerWidths } from "../components/drawerWidths";
import { Alert } from "./Alert";
import { DialogActionContext, useDialogActionScope } from "./dialogActionScope";
import { KEY_ESCAPE, KEY_TAB } from "./keyboardKeys";

const DEFAULT_MODAL_WIDTH_PX = 520;
const DEFAULT_LAYER_Z_INDEX = 1000;
const DRAWER_SIZE_LARGE = "large";
const DRAWER_SIZE_DEFAULT = "default";
const DRAWER_LARGE_VERTICAL_EXTENT_PX = 736;
const DRAWER_DEFAULT_VERTICAL_EXTENT_PX = 378;

/*
 * Ebenen des Kits (Plan 2026-10-13-antd-ausbau, Stufe 5): `Modal` und `Drawer` ohne Antd. Schnittstelle wie
 * Antd im genutzten Umfang. Kein natives `<dialog>`: dessen Top-Layer läge über allen Auswahllisten, die
 * an `document.body` hängen (Select, Datumsauswahl). Stattdessen Portal mit eigener Ebenenverwaltung:
 * nur die oberste Ebene reagiert auf Escape und hält den Fokus, die Seite scrollt nicht, solange eine
 * modale Ebene offen ist, und der Fokus kehrt beim Schließen zum Auslöser zurück.
 */

const EXIT_MS = 200;
const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"]), [contenteditable="true"]';

// ---------------------------------------------------------------- Ebenenverwaltung

const layerStack: number[] = [];
let nextLayerId = 1;
let scrollLocks = 0;
let savedOverflow = "";

function lockScroll() {
  if (scrollLocks++ === 0) {
    savedOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
  }
}
function unlockScroll() {
  if (scrollLocks > 0 && --scrollLocks === 0) document.body.style.overflow = savedOverflow;
}

/** Eine offene Ebene meldet sich an; nur die oberste schließt per Escape und hält den Fokus. */
function useLayer({ active, modal, keyboard, onEscape, panelRef }: {
  active: boolean;
  modal: boolean;
  keyboard: boolean;
  onEscape: (event: KeyboardEvent) => void;
  panelRef: React.RefObject<HTMLElement | null>;
}) {
  const escapeRef = React.useRef(onEscape);
  React.useEffect(() => { escapeRef.current = onEscape; });
  React.useEffect(() => {
    if (!active) return;
    const id = nextLayerId++;
    layerStack.push(id);
    if (modal) lockScroll();
    const onKey = (event: KeyboardEvent) => {
      if (layerStack[layerStack.length - 1] !== id) return;
      const panel = panelRef.current;
      if (event.key === KEY_ESCAPE) {
        // Offene Auswahllisten (Select, Datumsauswahl) schließen zuerst sich selbst.
        if (!keyboard || event.defaultPrevented) return;
        const target = event.target instanceof Element ? event.target : null;
        if (target?.closest('[aria-expanded="true"]')) return;
        if (!modal && panel && !panel.contains(document.activeElement)) return;
        escapeRef.current(event);
        return;
      }
      if (!modal || event.key !== KEY_TAB || !panel) return;
      const items = Array.from(panel.querySelectorAll<HTMLElement>(FOCUSABLE)).filter((node) => node.offsetParent !== null || node === document.activeElement);
      if (items.length === 0) { event.preventDefault(); panel.focus(); return; }
      const first = items[0];
      const last = items[items.length - 1];
      const inside = panel.contains(document.activeElement);
      if (event.shiftKey && (document.activeElement === first || !inside)) { event.preventDefault(); last.focus(); }
      else if (!event.shiftKey && (document.activeElement === last || !inside)) { event.preventDefault(); first.focus(); }
    };
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("keydown", onKey);
      const index = layerStack.indexOf(id);
      if (index >= 0) layerStack.splice(index, 1);
      if (modal) unlockScroll();
    };
  }, [active, keyboard, modal, panelRef]);
}

/**
 * Lebenszyklus einer Ebene: einmal geöffnet bleibt sie gerendert (Formularzustand bleibt erhalten),
 * außer `destroyOnHidden`. `entered` schaltet die Einblendung einen Frame nach dem Einhängen ein.
 */
function useLayerMotion(open: boolean, destroyOnHidden: boolean, forceRender: boolean, onAfterClose?: () => void, onAfterOpenChange?: (open: boolean) => void) {
  const [mounted, setMounted] = React.useState(open || forceRender);
  const [entered, setEntered] = React.useState(false);
  const [hidden, setHidden] = React.useState(!open);
  if (open && !mounted) setMounted(true);
  if (open && hidden) setHidden(false);
  const callbacks = React.useRef({ onAfterClose, onAfterOpenChange });
  React.useEffect(() => { callbacks.current = { onAfterClose, onAfterOpenChange }; });
  const wasOpen = React.useRef(open);
  React.useEffect(() => {
    if (open) {
      const frame = window.requestAnimationFrame(() => {
        setEntered(true);
        if (!wasOpen.current) callbacks.current.onAfterOpenChange?.(true);
        wasOpen.current = true;
      });
      return () => window.cancelAnimationFrame(frame);
    }
    setEntered(false);
    if (!wasOpen.current) return;
    const timer = window.setTimeout(() => {
      wasOpen.current = false;
      setHidden(true);
      if (destroyOnHidden) setMounted(false);
      callbacks.current.onAfterClose?.();
      callbacks.current.onAfterOpenChange?.(false);
    }, EXIT_MS);
    return () => window.clearTimeout(timer);
  }, [destroyOnHidden, open]);
  return { mounted, entered, hidden };
}

/** Fokus beim Öffnen in die Ebene, beim Schließen zurück zum Auslöser. */
function useFocusReturn(open: boolean, panelRef: React.RefObject<HTMLElement | null>, autoFocus: boolean) {
  const returnTo = React.useRef<HTMLElement | null>(null);
  React.useEffect(() => {
    if (!open) return;
    returnTo.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const frame = window.requestAnimationFrame(() => {
      const panel = panelRef.current;
      if (autoFocus && panel && !panel.contains(document.activeElement)) panel.focus({ preventScroll: true });
    });
    return () => {
      window.cancelAnimationFrame(frame);
      const panel = panelRef.current;
      const target = returnTo.current;
      if (target && target.isConnected && (!panel || panel.contains(document.activeElement) || document.activeElement === document.body)) {
        target.focus({ preventScroll: true });
      }
    };
  }, [autoFocus, open, panelRef]);
}

type Container = false | HTMLElement | (() => HTMLElement) | undefined;
function inContainer(node: ReactNode, container: Container) {
  if (container === false) return node;
  const target = typeof container === "function" ? container() : container ?? document.body;
  return createPortal(node, target);
}

type MaskConfig = boolean | { closable?: boolean; blur?: boolean; enabled?: boolean };
function readMask(mask: MaskConfig | undefined, maskClosable: boolean | undefined) {
  const config = typeof mask === "object" ? mask : { enabled: mask !== false };
  return { show: config.enabled !== false, closable: (typeof mask === "object" ? mask.closable : undefined) ?? maskClosable ?? true, blur: typeof mask === "object" && mask.blur === true };
}

type ClosableConfig = boolean | { closeIcon?: ReactNode; disabled?: boolean } | undefined;

// ---------------------------------------------------------------- Modal

export type ModalSemantic = "root" | "mask" | "wrapper" | "container" | "content" | "header" | "title" | "body" | "footer";
export type ModalStyles = Partial<Record<ModalSemantic, CSSProperties>>;
export type ModalClassNames = Partial<Record<ModalSemantic, string>>;

export interface ModalProps {
  open?: boolean;
  title?: ReactNode;
  children?: ReactNode;
  footer?: ReactNode | null | ((originNode: ReactNode, extra: { OkBtn: React.FC; CancelBtn: React.FC }) => ReactNode);
  onOk?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  onCancel?: (event: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement> | KeyboardEvent) => void;
  okText?: ReactNode;
  cancelText?: ReactNode;
  okType?: "primary" | "danger" | "default" | "dashed" | "link" | "text";
  okButtonProps?: ButtonProps;
  cancelButtonProps?: ButtonProps;
  confirmLoading?: boolean;
  /** Keep an interactive bank/approval flow visible while its action promise is pending. */
  pendingPresentation?: "background" | "visible";
  width?: number | string;
  centered?: boolean;
  closable?: ClosableConfig;
  closeIcon?: ReactNode;
  mask?: MaskConfig;
  maskClosable?: boolean;
  keyboard?: boolean;
  destroyOnHidden?: boolean;
  /** Antd-Altname für `destroyOnHidden`. */
  destroyOnClose?: boolean;
  forceRender?: boolean;
  afterClose?: () => void;
  afterOpenChange?: (open: boolean) => void;
  zIndex?: number;
  className?: string;
  rootClassName?: string;
  wrapClassName?: string;
  style?: CSSProperties;
  styles?: ModalStyles | ((info: { props: ModalProps }) => ModalStyles);
  classNames?: ModalClassNames;
  modalRender?: (node: ReactNode) => ReactNode;
  loading?: boolean;
  getContainer?: Container;
  autoFocus?: boolean;
  /** Antd-Optionen ohne Wirkung im Kit; der Fokus kehrt immer zum Auslöser zurück. */
  focusTriggerAfterClose?: boolean;
  focusable?: unknown;
  transitionName?: string;
  maskTransitionName?: string;
}

/** Dialog in der Bildmitte (Ersatz für Antd `Modal`); Escape, Schließen-Knopf und Klick daneben rufen `onCancel`. */
function ModalBase(props: ModalProps) {
  const {
    open = false, title, children, footer, onOk, onCancel, okText, cancelText, okType = "primary", okButtonProps, cancelButtonProps,
    confirmLoading, width = DEFAULT_MODAL_WIDTH_PX, centered, closable = true, closeIcon, mask, maskClosable, keyboard = true, destroyOnHidden, destroyOnClose,
    forceRender = false, afterClose, afterOpenChange, zIndex = DEFAULT_LAYER_Z_INDEX, className, rootClassName, wrapClassName, style, styles: stylesProp,
    classNames, modalRender, loading, getContainer, autoFocus = true, pendingPresentation = "background",
  } = props;
  const { text } = useUiText();
  const titleId = React.useId();
  const panelRef = React.useRef<HTMLDivElement>(null);
  const action = useDialogActionScope(open);
  const background = Boolean(confirmLoading) || action.pending;
  const visible = open && (pendingPresentation === "visible" || !background);
  const downOnWrap = React.useRef(false);
  const motion = useLayerMotion(open, Boolean(destroyOnHidden ?? destroyOnClose), forceRender, afterClose, afterOpenChange);
  const maskConfig = readMask(mask, maskClosable);
  const cancel = (event: Parameters<NonNullable<ModalProps["onCancel"]>>[0]) => onCancel?.(event);
  useLayer({ active: visible, modal: true, keyboard, onEscape: (event) => cancel(event), panelRef });
  useFocusReturn(visible, panelRef, autoFocus);
  if (!motion.mounted) return null;

  const styles = typeof stylesProp === "function" ? stylesProp({ props }) : stylesProp;
  const showClose = closable !== false && closeIcon !== null && !(typeof closable === "object" && closable.disabled);
  const icon = (typeof closable === "object" ? closable.closeIcon : undefined) ?? closeIcon ?? <CloseOutlined />;
  const OkBtn: React.FC = () => (
    <Button type={okType === "danger" ? "primary" : okType === "default" ? "default" : "primary"} danger={okType === "danger"} loading={confirmLoading} {...okButtonProps} onClick={(event) => onOk?.(event as React.MouseEvent<HTMLButtonElement>)}>
      {okText ?? text("OK")}
    </Button>
  );
  const CancelBtn: React.FC = () => (
    <DialogActionContext.Provider value={pendingPresentation === "visible" && background ? null : action.scope}>
      <Button {...cancelButtonProps} onClick={(event) => cancel(event as React.MouseEvent<HTMLElement>)}>{cancelText ?? text("Cancel")}</Button>
    </DialogActionContext.Provider>
  );
  const originFooter = <><CancelBtn /><OkBtn /></>;
  const footerNode = footer === null ? null : typeof footer === "function" ? footer(originFooter, { OkBtn, CancelBtn }) : footer === undefined ? originFooter : footer;
  const hasTitle = title != null && title !== false;

  const container = (
    <div className={cx("ck-modal-container", classNames?.container, classNames?.content)} style={{ ...styles?.container, ...styles?.content }}>
      {showClose ? (
        <button type="button" className="ck-modal-close" aria-label={text("Close")} onClick={(event) => cancel(event)}>
          <span className="ck-modal-close-x">{icon}</span>
        </button>
      ) : null}
      {hasTitle ? (
        <div className={cx("ck-modal-header", classNames?.header)} style={styles?.header}>
          <div className={cx("ck-modal-title", classNames?.title)} id={titleId} style={styles?.title}>{title}</div>
        </div>
      ) : null}
      <div className={cx("ck-modal-body", classNames?.body)} style={styles?.body}>
        {action.failed && <Alert type="error" showIcon message={text("Action failed")} />}
        {loading ? <div className="ck-modal-body-loading"><Spin /></div> : children}
      </div>
      {footerNode != null && footerNode !== false ? <div className={cx("ck-modal-footer", classNames?.footer)} style={styles?.footer}>{footerNode}</div> : null}
    </div>
  );

  return <DialogActionContext.Provider value={action.scope}>{inContainer(
    <div hidden={!visible} className={cx("ck-modal-root", motion.entered && "is-open", rootClassName, classNames?.root)} style={{ ...styles?.root, display: visible ? undefined : "none" }}>
      {maskConfig.show ? <div className={cx("ck-modal-mask", maskConfig.blur && "ck-modal-mask-blur", classNames?.mask)} style={{ zIndex, ...styles?.mask }} /> : null}
      <div
        className={cx("ck-modal-wrap", centered && "ck-modal-centered", wrapClassName, classNames?.wrapper)}
        style={{ zIndex, ...styles?.wrapper }}
        onMouseDown={(event) => { downOnWrap.current = event.target === event.currentTarget; }}
        onClick={(event) => {
          if (event.target === event.currentTarget && downOnWrap.current && maskConfig.show && maskConfig.closable) cancel(event);
          downOnWrap.current = false;
        }}
      >
        <div
          ref={panelRef}
          role="dialog"
          aria-modal="true"
          aria-labelledby={hasTitle ? titleId : undefined}
          tabIndex={-1}
          className={cx("ck-modal", className)}
          style={{ width, ...style }}
        >
          {modalRender ? modalRender(container) : container}
        </div>
      </div>
    </div>,
    getContainer,
  )}</DialogActionContext.Provider>;
}

// ---------------------------------------------------------------- Drawer

export type DrawerSemantic = "root" | "mask" | "wrapper" | "section" | "content" | "header" | "title" | "extra" | "body" | "footer";
export type DrawerStyles = Partial<Record<DrawerSemantic, CSSProperties>>;

export interface DrawerProps {
  /** Verarbeitung aus einer bestätigten Eingabe: Fläche ausblenden, Inhalt und Auftrag erhalten. */
  submitting?: boolean;
  open?: boolean;
  onClose?: (event: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement> | KeyboardEvent) => void;
  title?: ReactNode;
  extra?: ReactNode;
  footer?: ReactNode;
  children?: ReactNode;
  placement?: "left" | "right" | "top" | "bottom";
  /** Breite (links/rechts); Standard 378, `size="large"` 736. */
  width?: number | string;
  /** Höhe (oben/unten); Standard 378, `size="large"` 736. */
  height?: number | string;
  /** `"default"`, `"large"` oder eine Breite bzw. Höhe je nach Seite. */
  size?: "default" | "large" | number | string;
  closable?: ClosableConfig | { placement?: "start" | "end" };
  closeIcon?: ReactNode;
  mask?: MaskConfig;
  maskClosable?: boolean;
  keyboard?: boolean;
  destroyOnHidden?: boolean;
  destroyOnClose?: boolean;
  forceRender?: boolean;
  loading?: boolean;
  afterOpenChange?: (open: boolean) => void;
  zIndex?: number;
  className?: string;
  rootClassName?: string;
  rootStyle?: CSSProperties;
  style?: CSSProperties;
  styles?: DrawerStyles | ((info: { props: DrawerProps }) => DrawerStyles);
  classNames?: Partial<Record<DrawerSemantic, string>>;
  getContainer?: Container;
  autoFocus?: boolean;
  drawerRender?: (node: ReactNode) => ReactNode;
  /** Zugänglicher Name, wenn der Drawer keinen Titel zeigt (eigener Kopf im Inhalt). */
  "aria-label"?: string;
  /** Antd-Optionen ohne Wirkung im Kit. */
  push?: unknown;
  resizable?: unknown;
}

function drawerExtent(size: DrawerProps["size"], explicit: number | string | undefined, horizontal: boolean) {
  if (explicit !== undefined) return explicit;
  if (size === DRAWER_SIZE_LARGE) return horizontal ? drawerWidths.wide : DRAWER_LARGE_VERTICAL_EXTENT_PX;
  if (size === undefined || size === DRAWER_SIZE_DEFAULT) return horizontal ? drawerWidths.standard : DRAWER_DEFAULT_VERTICAL_EXTENT_PX;
  return size;
}

/** Seitenblatt (Ersatz für Antd `Drawer`); Kopf mit Schließen, Titel und Aktionen, Inhalt, Fußleiste. */
function DrawerBase(props: DrawerProps) {
  const {
    open = false, onClose, title, extra, footer, children, placement = "right", width, height, size, closable = true, closeIcon, mask,
    maskClosable, keyboard = true, destroyOnHidden, destroyOnClose, forceRender = false, loading, afterOpenChange, zIndex = DEFAULT_LAYER_Z_INDEX, className,
    rootClassName, rootStyle, style, styles: stylesProp, classNames, getContainer, autoFocus = true, drawerRender, "aria-label": ariaLabel, submitting,
  } = props;
  const { text } = useUiText();
  const titleId = React.useId();
  const panelRef = React.useRef<HTMLDivElement>(null);
  const action = useDialogActionScope(open);
  const background = Boolean(submitting) || action.pending;
  const visible = open && !background;
  const motion = useLayerMotion(open, Boolean(destroyOnHidden ?? destroyOnClose), forceRender, undefined, afterOpenChange);
  const maskConfig = readMask(mask, maskClosable);
  const close = (event: Parameters<NonNullable<DrawerProps["onClose"]>>[0]) => onClose?.(event);
  useLayer({ active: visible, modal: maskConfig.show, keyboard, onEscape: (event) => close(event), panelRef });
  useFocusReturn(visible, panelRef, autoFocus);
  if (!motion.mounted) return null;

  const styles = typeof stylesProp === "function" ? stylesProp({ props }) : stylesProp;
  const horizontal = placement === "left" || placement === "right";
  const extent = drawerExtent(size, horizontal ? width : height, horizontal);
  const showClose = closable !== false && closeIcon !== null && !(typeof closable === "object" && "disabled" in closable && closable.disabled);
  const icon = (typeof closable === "object" && "closeIcon" in closable ? closable.closeIcon : undefined) ?? closeIcon ?? <CloseOutlined />;
  const hasTitle = title != null && title !== false;
  const hasHeader = hasTitle || showClose || extra != null;

  const section = (
    <div
      ref={panelRef}
      role="dialog"
      aria-modal={maskConfig.show || undefined}
      aria-labelledby={hasTitle ? titleId : undefined}
      aria-label={hasTitle ? undefined : ariaLabel}
      tabIndex={-1}
      className={cx("ck-sheet-section", className, classNames?.section, classNames?.content)}
      style={{ ...style, ...styles?.section, ...styles?.content }}
    >
      {hasHeader ? (
        <div className={cx("ck-sheet-header", !hasTitle && !extra && "ck-sheet-header-close-only", classNames?.header)} style={styles?.header}>
          <div className="ck-sheet-header-title">
            {showClose ? (
              <button type="button" className="ck-sheet-close" aria-label={text("Close")} onClick={(event) => close(event)}>{icon}</button>
            ) : null}
            {hasTitle ? <div className={cx("ck-sheet-title", classNames?.title)} id={titleId} title={typeof title === "string" ? title : undefined} style={styles?.title}>{title}</div> : null}
          </div>
          {extra != null ? <div className={cx("ck-sheet-extra", classNames?.extra)} style={styles?.extra}>{extra}</div> : null}
        </div>
      ) : null}
      <div className={cx("ck-sheet-body", classNames?.body)} style={styles?.body}>
        {action.failed && <Alert type="error" showIcon message={text("Action failed")} />}
        {loading ? <div className="ck-sheet-body-loading"><Spin /></div> : children}
      </div>
      {footer != null && footer !== false ? <div className={cx("ck-sheet-footer", classNames?.footer)} style={styles?.footer}>{footer}</div> : null}
    </div>
  );

  return <DialogActionContext.Provider value={action.scope}>{inContainer(
    <div
      hidden={!visible} className={cx("ck-sheet", `ck-sheet-${placement}`, motion.entered && "ck-sheet-open", !maskConfig.show && "ck-sheet-modeless", rootClassName, classNames?.root)}
      style={{ zIndex, ...rootStyle, ...styles?.root, display: visible ? undefined : "none" }}
    >
      {maskConfig.show ? (
        <div
          className={cx("ck-sheet-mask", maskConfig.blur && "ck-sheet-mask-blur", classNames?.mask)}
          style={styles?.mask}
          aria-hidden="true"
          onClick={maskConfig.closable ? (event) => close(event) : undefined}
        />
      ) : null}
      <div className={cx("ck-sheet-content-wrapper", classNames?.wrapper)} style={{ ...(horizontal ? { width: extent } : { height: extent }), ...styles?.wrapper }}>
        {drawerRender ? drawerRender(section) : section}
      </div>
    </div>,
    getContainer,
  )}</DialogActionContext.Provider>;
}

export const Drawer = DrawerBase;
export { ModalBase };
